import { Link } from 'react-router-dom';
import { Heart, Star, ShoppingCart } from 'lucide-react';
import { motion } from 'framer-motion';
import { useWishlist } from '@/context/WishlistContext';
import { useCart } from '@/context/CartContext';
import { publicFetch } from '@/services/api.js';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

// Type for your real backend product (from trending or list endpoints)
interface RealProduct {
  productId: number;
  productName: string;
  categoryName: string;
  subCategoryName: string;
  rating: number;
  // Full product format (from /api/products/{id})
  variants?: Array<{
    offerPrice: number;
    originalPrice: number;
    discount: number;
    images?: string[];
  }>;
  details?: {
    brand?: string;
  };
  images?: string[];
  // Trending product format (from /api/products/trending)
  currentPrice?: number;
  originalPrice?: number;
  discount?: number;
  brand?: string;
  primaryImageUrl?: string;
}

interface ProductCardProps {
  product: RealProduct;
  className?: string;
}

export function ProductCard({ product, className = "" }: ProductCardProps) {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { addToCart } = useCart();

  // Handle both trending API format and full product API format
  const firstVariant = product.variants?.[0];
  const brand = product.details?.brand || product.brand || "Unknown Brand";
  
  // Check if this is trending API format or full product API format
  const isTrendingFormat = 'currentPrice' in product && 'primaryImageUrl' in product;
  
  const imageUrl = isTrendingFormat 
    ? product.primaryImageUrl
    : (firstVariant?.images?.[0] ||
       product.images?.[0] ||
       'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzlmYTZiNyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9IjAuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=');

  const normalizedProduct = {
    id: String(product.productId), // Important: convert number → string for URL
    name: product.productName,
    brand,
    // Use trending format prices if available, otherwise use variant prices
    price: isTrendingFormat ? product.currentPrice : (firstVariant?.offerPrice || 0),
    originalPrice: isTrendingFormat ? product.originalPrice : (firstVariant?.originalPrice || 0),
    discount: isTrendingFormat ? product.discount : (firstVariant?.discount || 0),
    rating: product.rating || 0,
    image: imageUrl,
    category: product.categoryName,
    subcategory: product.subCategoryName,
  };



  const inWishlist = isInWishlist(product.productId);

  const handleWishlistClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await toggleWishlist(product.productId);
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      // First, fetch the product details to get the actual variant ID
      const productId = normalizedProduct.id;
      const response = await publicFetch(`/api/products/${productId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch product details: ${response.status}`);
      }
      
      const productDetails = await response.json();
      
      // Find the first available variant
      const variants = productDetails.variants || [];
      if (variants.length === 0) {
        throw new Error('No variants available for this product');
      }
      
      // Use first variant with stock > 0, or just first variant
      const firstVariant = variants.find(v => (v.stockQuantity || 0) > 0) || variants[0];
      
      if (!firstVariant) {
        throw new Error('No available variant found');
      }
      
      // Build cart item structure matching mobile app with ACTUAL variant ID
      const cartItem = {
        variantId: firstVariant.variantId, // Now using actual variant ID!
        productId: Number(productId),
        productName: normalizedProduct.name,
        salePrice: Number(firstVariant.offerPrice || firstVariant.originalPrice || normalizedProduct.price),
        originalPrice: firstVariant.originalPrice ? Number(firstVariant.originalPrice) : undefined,
        offerPrice: firstVariant.offerPrice ? Number(firstVariant.offerPrice) : undefined,
        size: firstVariant.size?.code || firstVariant.sizeCode || 'M',
        sizeName: firstVariant.size?.name || firstVariant.sizeName || 'Medium',
        color: firstVariant.color || 'Default',
        sku: firstVariant.sku || `SKU-${productId}`,
        stockQuantity: firstVariant.stockQuantity || 0,
        images: [{
          id: 1,
          imageUrl: normalizedProduct.image,
          imageType: 'PRIMARY',
          displayOrder: 1,
        }],
        productCategoryName: productDetails.categoryName || normalizedProduct.category || null,
        productSubCategoryName: productDetails.subCategoryName || normalizedProduct.subcategory || null,
        vendor: productDetails.vendor ? {
          vendorId: productDetails.vendor.vendorId || productDetails.vendorId,
          vendorName: productDetails.vendor.vendorName || productDetails.vendorName || ''
        } : undefined,
        seller: {
          id: productDetails.seller?.id || productDetails.seller?.sellerId || null,
          shopName: productDetails.seller?.shopName || productDetails.seller?.sellerName || 'AuraKart',
          city: productDetails.seller?.city || productDetails.sellerCity || 'Chennai',
          state: productDetails.seller?.state || productDetails.sellerState || 'Tamil Nadu',
        },
      };

      await addToCart(cartItem);
      
      // Optional: Show success message
      alert(`${normalizedProduct.name} added to cart!`);
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add item to cart. Please try again.');
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  };

  return (
    <motion.div
      className={`group bg-card rounded-xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300 hover:scale-105 flex flex-col h-full ${className}`}
      whileHover={{ y: -4 }}
    >
      <Link to={`/product/${normalizedProduct.id}`} className="flex-1 flex flex-col">
        <div className="relative aspect-[3/4] overflow-hidden bg-secondary">
          <img
            src={normalizedProduct.image}
            alt={normalizedProduct.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => {
              const target = e.currentTarget;
              if (!target.src.includes('data:image')) {
                target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzlmYTZiNyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9IjAuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
              }
            }}
          />

          {/* Discount Badge */}
          {normalizedProduct.discount > 0 && (
            <div className="absolute top-3 left-3">
              <Badge className="bg-red-500 hover:bg-red-600 text-white">
                {Math.round(normalizedProduct.discount)}% OFF
              </Badge>
            </div>
          )}

          {/* Wishlist Button */}
          <button
            onClick={handleWishlistClick}
            className={cn(
              "absolute top-3 right-3 p-2 rounded-full transition-all duration-200",
              inWishlist
                ? "bg-primary text-primary-foreground"
                : "bg-card/80 text-foreground hover:bg-card"
            )}
          >
            <Heart className={cn("h-4 w-4", inWishlist && "fill-current")} />
          </button>

          {/* Rating Badge */}
          {normalizedProduct.rating > 0 && (
            <div className="absolute bottom-3 left-3 bg-foreground/80 text-card text-xs font-medium px-2 py-1 rounded flex items-center gap-1">
              <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
              <span>{normalizedProduct.rating.toFixed(1)}</span>
            </div>
          )}
        </div>

        <div className="p-4 space-y-2 flex-1 flex flex-col">
          {/* Brand */}
          <p className="text-xs text-muted-foreground uppercase tracking-wider">
            {normalizedProduct.brand}
          </p>

          {/* Product Name - Fixed height with line clamping */}
          <h3 className="font-semibold text-sm line-clamp-2 text-gray-900 group-hover:text-primary min-h-[2.5rem]">
            {normalizedProduct.name}
          </h3>

          {/* Category */}
          <p className="text-xs text-muted-foreground">
            {normalizedProduct.category} • {normalizedProduct.subcategory}
          </p>

          {/* Price - Push to bottom */}
          <div className="flex items-center gap-2 pt-2 mt-auto">
            <span className="font-bold text-lg text-gray-900">
              {formatPrice(normalizedProduct.price)}
            </span>
            {normalizedProduct.originalPrice > normalizedProduct.price && (
              <span className="text-sm text-muted-foreground line-through">
                {formatPrice(normalizedProduct.originalPrice)}
              </span>
            )}
          </div>
        </div>
      </Link>
      
      {/* Add to Cart Button - Outside Link to prevent navigation - Fixed position */}
      <div className="px-4 pb-4 mt-auto">
        <button
          onClick={handleAddToCart}
          className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md font-medium text-sm transition-colors hover:bg-primary/90 flex items-center justify-center gap-2"
        >
          <ShoppingCart className="w-4 h-4" />
          Add to Cart
        </button>
      </div>
    </motion.div>
  );
}